import type { RiskClass } from "../actions/envelope.js";
import type { ActionPolicy, Condition, PolicyModifications, PolicyRule } from "./ruleSchema.js";
import type { ReasonCode } from "./reasonCodes.js";

export type PolicyEvaluationContext = {
  environment: "sandbox" | "production";
  amount: number | null;
  riskLevel: "low" | "medium" | "high" | null;
  customerTier: "free" | "pro" | "enterprise" | "smb" | null;
  actionSubtype: string | null;
  riskClass: RiskClass | null;
  originSystem: string | null;
  chainAmountSum: number | null;
};

export type PolicyEvaluationResult = {
  decision: "allow" | "deny" | "require_approval" | "modify";
  reasonCode: ReasonCode;
  modifications?: PolicyModifications;
  approverGroupId?: string | null;
  permitTtlSeconds?: number | null;
};

const compare = (left: string | number, op: Condition["op"], right: unknown): boolean => {
  if (op === "eq") {
    return left === right;
  }
  if (op === "neq") {
    return left !== right;
  }
  if (typeof left !== "number" || typeof right !== "number") {
    return false;
  }
  if (op === "gt") {
    return left > right;
  }
  if (op === "gte") {
    return left >= right;
  }
  if (op === "lt") {
    return left < right;
  }
  if (op === "lte") {
    return left <= right;
  }
  return false;
};

const contextValueForField = (
  field: Condition["field"],
  ctx: PolicyEvaluationContext
): string | number | null => {
  if (field === "amount") {
    return ctx.amount;
  }
  if (field === "environment") {
    return ctx.environment;
  }
  if (field === "risk_level") {
    return ctx.riskLevel;
  }
  if (field === "customer_tier") {
    return ctx.customerTier;
  }
  if (field === "action_subtype") {
    return ctx.actionSubtype;
  }
  if (field === "risk_class") {
    return ctx.riskClass;
  }
  if (field === "origin_system") {
    return ctx.originSystem;
  }
  if (field === "chain_amount_sum") {
    return ctx.chainAmountSum;
  }
  return null;
};

const matchesCondition = (condition: Condition, ctx: PolicyEvaluationContext): boolean => {
  const ctxValue = contextValueForField(condition.field, ctx);
  if (ctxValue === null || ctxValue === undefined) {
    return false;
  }
  if (condition.op === "in" || condition.op === "not_in") {
    const arr = Array.isArray(condition.value) ? condition.value : [];
    const hit = arr.some((v) => v === ctxValue);
    return condition.op === "in" ? hit : !hit;
  }
  return compare(ctxValue, condition.op, condition.value);
};

const mapThen = (
  rule: PolicyRule
): {
  decision: "allow" | "deny" | "require_approval" | "modify";
  reasonCode: ReasonCode;
  modifications?: PolicyModifications;
  approverGroupId?: string | null;
  permitTtlSeconds?: number | null;
} => {
  if (rule.then === "allow") {
    return { decision: "allow", reasonCode: "RULE_MATCH_ALLOW" };
  }
  if (rule.then === "deny") {
    return { decision: "deny", reasonCode: "RULE_MATCH_DENY" };
  }
  if (rule.then === "require_modified_parameters") {
    return {
      decision: "modify",
      reasonCode: "RULE_MATCH_MODIFY",
      modifications: rule.modifications
    };
  }
  return {
    decision: "require_approval",
    reasonCode: "RULE_MATCH_REQUIRE_APPROVAL",
    approverGroupId: rule.approver_group_id ?? null,
    permitTtlSeconds: rule.permit_ttl_seconds ?? null
  };
};

const mapDefault = (decision: "allow" | "deny" | "require_approval"): PolicyEvaluationResult => {
  if (decision === "allow") {
    return { decision: "allow", reasonCode: "RULE_DEFAULT_ALLOW" };
  }
  if (decision === "deny") {
    return { decision: "deny", reasonCode: "RULE_DEFAULT_DENY" };
  }
  return { decision: "require_approval", reasonCode: "RULE_DEFAULT_REQUIRE_APPROVAL" };
};

/** Index of first matching conditional rule, or null if default / auto-allow / flat mode */
export const findMatchedRuleIndex = (
  policy: ActionPolicy,
  ctx: PolicyEvaluationContext
): number | null => {
  if (policy.mode !== "conditional") {
    return null;
  }
  if (policy.auto_allow_low_risk === true && (ctx.riskClass === "low" || ctx.riskLevel === "low")) {
    return null;
  }
  const rules = policy.rules ?? [];
  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i]!;
    if (rule.when.every((c) => matchesCondition(c, ctx))) {
      return i;
    }
  }
  return null;
};

export const evaluatePolicy = (
  policy: ActionPolicy,
  ctx: PolicyEvaluationContext
): PolicyEvaluationResult => {
  if (policy.mode === "allow") {
    return { decision: "allow", reasonCode: "POLICY_ALLOW" };
  }
  if (policy.mode === "deny") {
    return { decision: "deny", reasonCode: "POLICY_DENY" };
  }
  if (policy.mode === "require_approval") {
    return { decision: "require_approval", reasonCode: "POLICY_REQUIRE_APPROVAL" };
  }
  if (policy.auto_allow_low_risk === true && (ctx.riskClass === "low" || ctx.riskLevel === "low")) {
    return { decision: "allow", reasonCode: "AUTO_ALLOW_LOW_RISK" };
  }
  const rules = policy.rules ?? [];
  for (const rule of rules) {
    const allMatch = rule.when.every((c) => matchesCondition(c, ctx));
    if (allMatch) {
      return mapThen(rule);
    }
  }
  const def = policy.default_decision ?? "deny";
  return mapDefault(def);
};
